import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import { api, imageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

export default function MotorcycleForm() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    age_years: "",
    pickup_location: "",
    can_deliver: false,
    delivery_note: "",
    price_per_day: 600,
    price_per_2_weeks: 6500,
    price_per_month: 12000,
    insurance_amount: 5000,
    description: "",
    photos: [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/motorcycles/${id}`).then(({ data }) => {
      setForm({
        name: data.name,
        age_years: data.age_years ?? "",
        pickup_location: data.pickup_location,
        can_deliver: data.can_deliver,
        delivery_note: data.delivery_note || "",
        price_per_day: data.price_per_day,
        price_per_2_weeks: data.price_per_2_weeks,
        price_per_month: data.price_per_month,
        insurance_amount: data.insurance_amount,
        description: data.description || "",
        photos: data.photos || [],
      });
    });
  }, [id, isEdit]);

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      const paths = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((p) => ({ ...p, photos: [...p.photos, ...paths] }));
      toast.success(`${paths.length} photo(s) uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const removeImg = (path) => setForm((p) => ({ ...p, photos: p.photos.filter((x) => x !== path) }));

  const onSave = async (e) => {
    e.preventDefault();
    if (form.photos.length < 2) {
      toast.error("Please upload at least 2 photos.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        age_years: form.age_years === "" ? null : Number(form.age_years),
        pickup_location: form.pickup_location,
        can_deliver: !!form.can_deliver,
        delivery_note: form.delivery_note || null,
        price_per_day: Number(form.price_per_day),
        price_per_2_weeks: Number(form.price_per_2_weeks),
        price_per_month: Number(form.price_per_month),
        insurance_amount: Number(form.insurance_amount),
        description: form.description || null,
        photos: form.photos,
      };
      if (isEdit) {
        await api.put(`/motorcycles/${id}`, payload);
        toast.success("Motorcycle updated");
      } else {
        await api.post("/motorcycles", payload);
        toast.success("Motorcycle published");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (authLoading) return <div className="min-h-screen"><Navbar /></div>;
  if (!user) { nav("/dashboard"); return null; }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <div className="overline">{isEdit ? "Edit motorcycle" : "New motorcycle"}</div>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl tracking-tight mb-10">
          {isEdit ? "Refine your bike listing" : "List a motorcycle for rent"}
        </h1>

        <form onSubmit={onSave} className="space-y-10">
          {/* Basics */}
          <section className="surface-card p-8">
            <h2 className="font-serif-display text-2xl mb-6">Bike details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="overline">Motorcycle name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-earthy mt-2" placeholder="e.g. Royal Enfield Classic 350" data-testid="moto-form-name" />
              </div>
              <div>
                <label className="overline">Age (years, optional)</label>
                <input type="number" min={0} value={form.age_years}
                  onChange={(e) => setForm({ ...form, age_years: e.target.value })}
                  className="input-earthy mt-2" placeholder="e.g. 3" data-testid="moto-form-age" />
              </div>
              <div>
                <label className="overline">Pickup location</label>
                <input required value={form.pickup_location}
                  onChange={(e) => setForm({ ...form, pickup_location: e.target.value })}
                  className="input-earthy mt-2" placeholder="e.g. Sector 12, Kharghar" data-testid="moto-form-pickup" />
              </div>
              <div className="md:col-span-2">
                <label className="overline">Description (optional)</label>
                <textarea rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-earthy mt-2 resize-none" placeholder="Condition, mileage, helmets included, etc."
                  data-testid="moto-form-description" />
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.can_deliver}
                    onChange={(e) => setForm({ ...form, can_deliver: e.target.checked })}
                    data-testid="moto-form-can-deliver" />
                  <span>Available for delivery to the stay</span>
                </label>
              </div>
              {form.can_deliver && (
                <div className="md:col-span-2">
                  <label className="overline">Delivery note (optional)</label>
                  <input value={form.delivery_note}
                    onChange={(e) => setForm({ ...form, delivery_note: e.target.value })}
                    className="input-earthy mt-2" placeholder="e.g. Free within 10km, ₹200 beyond"
                    data-testid="moto-form-delivery-note" />
                </div>
              )}
            </div>
          </section>

          {/* Pricing */}
          <section className="surface-card p-8">
            <h2 className="font-serif-display text-2xl mb-6">Pricing & insurance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                ["price_per_day", "Per day"],
                ["price_per_2_weeks", "Per 2 weeks"],
                ["price_per_month", "Per month"],
                ["insurance_amount", "Insurance / deposit"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="overline">{l}</label>
                  <input type="number" min={0} required value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="input-earthy mt-2" data-testid={`moto-form-${k}`} />
                </div>
              ))}
            </div>
          </section>

          {/* Photos */}
          <section className="surface-card p-8">
            <h2 className="font-serif-display text-2xl mb-2">Photos</h2>
            <p className="text-xs mb-5" style={{ color: "var(--text-2)" }}>
              Upload at least <strong>2 photos</strong> of the bike (left side, right side, odometer, etc.).
            </p>
            <label
              htmlFor="moto-file-input"
              className="flex flex-col items-center justify-center rounded-xl p-8 cursor-pointer transition-colors"
              style={{ border: "2px dashed var(--line)", background: "var(--bone)" }}
              data-testid="moto-upload-dropzone"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-6 h-6 text-[color:var(--forest)]" />}
              <span className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>
                {uploading ? "Uploading…" : "Click to upload or drop files here"}
              </span>
              <input id="moto-file-input" type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => uploadFiles(e.target.files)} data-testid="moto-upload-input" />
            </label>
            {form.photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-5">
                {form.photos.map((p, i) => (
                  <div key={p} className="relative rounded-xl overflow-hidden" data-testid={`moto-photo-${i}`}>
                    <img src={imageUrl(p)} alt="" className="w-full h-32 object-cover" />
                    <button type="button" onClick={() => removeImg(p)}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90"
                      data-testid={`moto-remove-photo-${i}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs" style={{ color: form.photos.length < 2 ? "#9E4747" : "var(--text-3)" }}>
              {form.photos.length}/2 photos uploaded
            </p>
          </section>

          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => nav("/dashboard")} className="btn-pill btn-outline-dark" data-testid="moto-cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-pill btn-primary" data-testid="moto-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEdit ? "Save changes" : "Publish motorcycle"}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
